import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { type SxProps, styled, type Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { type Props as ImageProps, Image as UiImage } from '../Image/Image';

type Props = ImageProps & {
  placeholderSrc?: string;
  src: string;
  showLoader?: boolean;
  sx?: SxProps<Theme>;
  zoomOnHover?: boolean;
  minHeight?: string | number;
};

const Container = styled(Box, {
  shouldForwardProp: (prop) => !['rounded', 'minHeight'].includes(prop as string),
})<Pick<Props, 'rounded' | 'minHeight'>>(({ rounded, theme, minHeight }) => {
  const numericRadius =
    typeof theme.shape.borderRadius === 'number'
      ? theme.shape.borderRadius
      : Number.parseFloat(theme.shape.borderRadius);
  const baseRadius = Number.isFinite(numericRadius) ? numericRadius : 0;

  return {
    borderRadius: rounded ? baseRadius * 2 : 0,
    display: 'flex',
    height: 'inherit',
    minHeight,
    overflow: 'hidden',
    width: 'inherit',
  };
});

const LoaderContainer = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
});

export const ProgressiveImage = ({
  placeholderSrc,
  src,
  sx = {},
  showLoader = true,
  zoomOnHover = false,
  minHeight = 'auto',
  ...props
}: Props) => {
  const [imgSrc, setImgSrc] = useState(placeholderSrc || src);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImgSrc(placeholderSrc || src);

    const img = new Image();
    img.src = src;

    const handleLoad = () => {
      setImgSrc(src);
      setIsLoading(false);
    };

    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener('load', handleLoad);
    }

    return () => {
      img.removeEventListener('load', handleLoad);
    };
  }, [src, placeholderSrc]);

  return (
    <Container
      rounded={props.rounded}
      minHeight={minHeight}
      sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', position: 'relative' }}
    >
      {showLoader && isLoading ? (
        <LoaderContainer>
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </LoaderContainer>
      ) : null}
      <UiImage
        {...props}
        src={imgSrc}
        sx={{
          '&:hover': {
            transform: zoomOnHover ? 'scale(1.2)' : 'none',
          },
          ...(placeholderSrc && imgSrc === placeholderSrc
            ? {
                clipPath: 'inset(0)',
                filter: 'blur(10px)',
                opacity: 0.5,
                ...sx,
              }
            : {
                filter: 'none',
                opacity: isLoading ? 0 : 1,
                transition: (theme: Theme) =>
                  theme.transitions.create(['filter', 'opacity', 'transform'], {
                    duration: theme.transitions.duration.complex,
                    easing: theme.transitions.easing.easeInOut,
                  }),
                ...sx,
              }),
        }}
      />
    </Container>
  );
};
